import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  FileBox,
  ShieldCheck,
  Trash2,
  Lock,
  ArrowRight,
  FileText,
  Zap,
  Upload,
  MonitorCheck,
  ListChecks,
  DownloadCloud,
  CheckCircle2,
  Info,
  User
} from "lucide-react";
import { cn } from "@/lib/utils";

const schema = z.object({
  email: z.string().trim().email("Email inválido").max(255),
  password: z.string().min(1, "Informe a senha").max(72),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/upload";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Força Light Mode na Landing Page
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }

    // Check for session conflict error
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "session_conflict") {
      toast({
        title: "Sessão encerrada",
        description: "Sua conta foi acessada em outro dispositivo. O acesso nesta máquina foi desconectado.",
        variant: "destructive",
      });
    }

    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, [location.search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      toast({ title: "Verifique os campos", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword(parsed.data);
    setLoading(false);
    if (error) {
      toast({ title: "Falha no login", description: "Email ou senha incorretos.", variant: "destructive" });
      return;
    }
    navigate(from, { replace: true });
  }

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden">

      {/* Glass background orbs (depth + volume) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF55,transparent_60%)] blur-3xl" />
        <div className="absolute top-[20%] -right-40 size-[600px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC44,transparent_60%)] blur-3xl" />
        <div className="absolute top-[55%] left-[20%] size-[480px] rounded-full bg-[radial-gradient(circle_at_center,#7DD3FC55,transparent_65%)] blur-3xl" />
        <div className="absolute bottom-[-10%] right-[10%] size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#A5C8FF55,transparent_65%)] blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(255,255,255,0.4))]" />
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/60 backdrop-blur-xl border-b border-white/60 shadow-[0_8px_30px_rgba(31,64,128,0.06)]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl grid place-items-center bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] shadow-[0_4px_16px_rgba(51,153,255,0.45)]">
              <FileBox className="size-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-lg leading-tight tracking-tight text-slate-900 dark:text-slate-50">Extrator Fiscal</div>
              <div className="text-[10px] uppercase font-semibold text-slate-500 dark:text-slate-400 tracking-wider">NF-e / NFC-e</div>
            </div>
          </div>

          <nav aria-label="Navegação principal" className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-400">
            <a href="#recursos" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Recursos</a>
            <Link to="/planos" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Planos</Link>
            <a href="#como-funciona" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Como funciona</a>
            <a href="#seguranca" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Segurança</a>
            <a href="#contato" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Contato</a>
          </nav>

          <div className="flex items-center gap-4">
            <Button asChild className="bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] hover:opacity-90 border-0 text-white shadow-lg shadow-blue-500/25">
              <Link to="/signup">Criar conta</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="animate-in fade-in duration-1000">
        <section aria-labelledby="hero-heading" className="relative pt-8 pb-16 lg:pt-12 lg:pb-24 overflow-hidden">

          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center relative z-10">
            {/* Esquerda - Copy */}
            <div className="space-y-6 lg:space-y-8">
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-white/5 border border-blue-100 dark:border-white/10 text-blue-700 dark:text-[#9DDDFF] text-xs font-semibold tracking-wide">
                  <ShieldCheck className="size-4" />
                  SOFTWARE DE PROCESSAMENTO DE DADOS
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-semibold tracking-wide">
                  ⚠️ Ferramenta de TI, não somos contabilidade
                </div>
              </div>
              
              <h1 id="hero-heading" className="text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-tight text-slate-900">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600">Extraia, estruture e</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-[#33CCFF]">inspecione dados</span>
                <span className="text-slate-900">com foco em segurança.</span>
              </h1>
              
              <p className="text-base lg:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
                O Extrator Fiscal processa seus arquivos XML localmente no seu navegador, organizando as informações tributárias e apontando indícios de inconsistência.
                Os arquivos não são armazenados após o uso.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] hover:opacity-90 border-0 text-white shadow-lg shadow-blue-500/25 px-6 text-sm h-11 lg:h-12 lg:px-8 lg:text-base">
                  <Link to="/planos">Começar agora <ArrowRight className="ml-2 size-4" /></Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-8 text-base border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 backdrop-blur-md">
                  <a href="#como-funciona">Saiba mais</a>
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-blue-100 mt-6 lg:mt-10 animate-in slide-in-from-left duration-700 delay-300 fill-mode-both">
                <div className="flex items-center sm:flex-col sm:items-start gap-3 sm:gap-2 group">
                  <ShieldCheck className="size-6 text-[#3399FF] shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-sm sm:text-xs font-bold text-slate-700 leading-tight">Processamento local</span>
                </div>
                <div className="flex items-center sm:flex-col sm:items-start gap-3 sm:gap-2 group">
                  <Trash2 className="size-6 text-[#3399FF] shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-sm sm:text-xs font-bold text-slate-700 leading-tight">Zero persistência</span>
                </div>
                <div className="flex items-center sm:flex-col sm:items-start gap-3 sm:gap-2 group">
                  <Lock className="size-6 text-[#3399FF] shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-sm sm:text-xs font-bold text-slate-700 leading-tight">Controle pelo usuário</span>
                </div>
              </div>
            </div>

            <div className="relative w-full max-w-sm mx-auto lg:ml-auto animate-in slide-in-from-right duration-700 delay-200 fill-mode-both">
              {/* glass card with 3D depth */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/30 via-white/0 to-[#1A66CC]/25 rounded-[2.5rem] blur-2xl opacity-80" aria-hidden="true" />
              <div className="relative bg-white/85 backdrop-blur-3xl rounded-[2rem] p-8 lg:p-10 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.45),inset_0_1px_1px_rgba(255,255,255,1)] border border-blue-200/50 z-10 transition-all duration-500 hover:shadow-[0_40px_100px_-20px_rgba(26,102,204,0.55),inset_0_1px_1px_rgba(255,255,255,1)] hover:-translate-y-1">
                <div className="mb-8">
                  <div className="size-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-600 mb-4 shadow-inner ring-1 ring-blue-500/10">
                    <User className="size-6" />
                  </div>
                  <h2 className="text-2xl font-black tracking-tight text-slate-900">Acesse sua conta</h2>
                  <p className="text-xs font-bold text-slate-500/80 mt-1 uppercase tracking-widest">Painel Operacional</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">E-mail</Label>
                    <div className="relative">
                      <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required 
                        className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 transition-all"
                        placeholder="seu@email.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Senha</Label>
                      <Link to="/forgot-password" className="text-xs font-semibold text-blue-600 dark:text-[#33CCFF] hover:text-blue-700 dark:hover:text-blue-300 transition-colors">Esqueci minha senha</Link>
                    </div>
                    <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required 
                      className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 transition-all"
                      placeholder="••••••••" />
                  </div>
                  <Button type="submit" className="w-full h-12 text-base font-bold bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] hover:opacity-90 text-white shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]" disabled={loading}>
                    {loading ? "Entrando..." : "Entrar"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Não tem conta? <Link to="/signup" className="text-blue-600 dark:text-[#33CCFF] font-semibold hover:underline">Criar conta</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Banner Alert */}
        <div className="max-w-5xl mx-auto px-4 lg:px-6 -mt-8 lg:-mt-12 relative z-20 animate-in zoom-in duration-500 delay-500 fill-mode-both">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-[0_20px_50px_-12px_rgba(26,102,204,0.25),inset_0_1px_0_rgba(255,255,255,0.9)] border border-white/70 flex flex-col sm:flex-row items-center justify-between gap-6 transition-all hover:shadow-[0_30px_70px_-12px_rgba(26,102,204,0.35)] hover:-translate-y-1">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="size-12 rounded-full bg-gradient-to-br from-[#33CCFF]/20 to-[#1A66CC]/10 grid place-items-center shrink-0 shadow-inner ring-1 ring-white/80">
                <ShieldCheck className="size-6 text-[#3399FF]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Processamento seguro e em tempo real</h3>
                <p className="text-sm text-slate-500 mt-0.5">Execução 100% local no seu navegador. Nenhum arquivo é armazenado.</p>
              </div>
            </div>
            <Button variant="ghost" className="text-[#3399FF] hover:bg-blue-50 font-bold" asChild>
              <a href="#seguranca">Ler mais <ArrowRight className="ml-2 size-4" /></a>
            </Button>
          </div>
        </div>

        {/* Recursos */}
        <section id="recursos" aria-labelledby="recursos-heading" className="py-16 lg:py-24 relative z-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 id="recursos-heading" className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">Recursos da Plataforma</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: FileText, title: "Suporte completo", desc: "NF-e e NFC-e em lote, de diversos emissores.", delay: "delay-[100ms]", color: "from-[#33CCFF] to-[#0EA5E9]" },
                { icon: ShieldCheck, title: "Inspeção sistêmica", desc: "Tributos, CFOP, produtos e indícios de falha.", delay: "delay-[200ms]", color: "from-[#1A66CC] to-[#2563EB]" },
                { icon: Zap, title: "Processamento rápido", desc: "Resultados em segundos, sem filas ou espera.", delay: "delay-[300ms]", color: "from-[#7C3AED] to-[#6D28D9]" },
                { icon: Lock, title: "Privacidade total", desc: "Processamento local dos dados no navegador.", delay: "delay-[400ms]", color: "from-[#059669] to-[#047857]" },
              ].map((f, i) => (
                <article key={i} className={cn(
                  "bg-white/80 backdrop-blur-xl p-7 rounded-2xl shadow-[0_20px_50px_-15px_rgba(26,102,204,0.2),inset_0_1px_0_rgba(255,255,255,1)] border border-slate-200/60 transition-all duration-500 group relative overflow-hidden",
                  "hover:shadow-[0_35px_70px_-15px_rgba(26,102,204,0.35)] hover:-translate-y-3 hover:scale-[1.02] hover:border-blue-200/80",
                  "animate-in fade-in slide-in-from-bottom-8 fill-mode-both",
                  f.delay
                )}>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r opacity-60 rounded-t-2xl" style={{background: `linear-gradient(to right, var(--tw-gradient-stops))`}} />
                  <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${f.color} rounded-t-2xl`} />
                  <div className="flex items-start justify-between mb-5">
                    <div className={`size-14 rounded-2xl bg-gradient-to-br ${f.color} grid place-items-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <f.icon className="size-7 text-white" />
                    </div>
                    <span className="text-4xl font-black text-slate-100 leading-none select-none">{String(i+1).padStart(2,'0')}</span>
                  </div>
                  <h3 className="text-lg font-black text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Como funciona */}
        <section id="como-funciona" aria-labelledby="como-funciona-heading" className="py-16 lg:py-24 relative z-10">
          <div className="absolute inset-0 bg-white/30 dark:bg-[#060a13]/80 backdrop-blur-2xl border-y border-white/60 dark:border-white/10 -z-10" />
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-blue-600 dark:text-[#33CCFF] uppercase mb-2">Fluxo Transparente</div>
              <h2 id="como-funciona-heading" className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">Como Funciona</h2>
            </div>

            <div className="relative">
              {/* Linha conectora entre steps - visível apenas em desktop */}
              <div className="hidden md:block absolute top-[3.25rem] left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#33CCFF] via-[#1A66CC] to-[#33CCFF] opacity-20 z-0" />
              <div className="grid md:grid-cols-4 gap-6 relative z-10">
                {[
                  { step: 1, icon: Upload, title: "Upload do XML", desc: "Envie o arquivo XML da NF-e ou NFC-e diretamente no navegador.", delay: "delay-[150ms]" },
                  { step: 2, icon: MonitorCheck, title: "Processamento local", desc: "O arquivo é processado no seu navegador. Nada é enviado aos servidores.", delay: "delay-[250ms]" },
                  { step: 3, icon: ListChecks, title: "Inspeção completa", desc: "Dados, tributos, produtos e indícios de inconsistência são organizados.", delay: "delay-[350ms]" },
                  { step: 4, icon: DownloadCloud, title: "Exportação segura", desc: "Seus dados ficam com você. Exporte quando quiser.", delay: "delay-[450ms]" },
                ].map((s, i) => (
                  <article key={i} className={cn(
                    "flex flex-col items-center text-center group transition-all duration-500",
                    "animate-in fade-in zoom-in-95 fill-mode-both",
                    s.delay
                  )}>
                    <div className="size-12 rounded-full bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] text-white font-black text-lg grid place-items-center mb-5 shadow-[0_10px_30px_-5px_rgba(26,102,204,0.5),inset_0_1px_0_rgba(255,255,255,0.4)] relative z-10 ring-4 ring-white group-hover:scale-110 group-hover:shadow-[0_15px_35px_-5px_rgba(26,102,204,0.7)] transition-all duration-300">
                      {s.step}
                    </div>
                    <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 shadow-[0_20px_50px_-15px_rgba(26,102,204,0.15),inset_0_1px_0_rgba(255,255,255,1)] p-7 rounded-2xl w-full h-full transition-all duration-500 group-hover:shadow-[0_30px_60px_-15px_rgba(26,102,204,0.3)] group-hover:-translate-y-2 group-hover:border-blue-200/60">
                      <div className="size-14 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 grid place-items-center mx-auto mb-4 group-hover:from-[#33CCFF]/20 group-hover:to-[#1A66CC]/20 transition-all duration-500">
                        <s.icon aria-hidden="true" className="size-7 text-[#1A66CC] group-hover:scale-110 transition-transform" />
                      </div>
                      <h3 className="text-base font-black text-slate-900 mb-2">{s.title}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Segurança */}
        <section id="seguranca" aria-labelledby="seguranca-heading" className="py-16 lg:py-24 relative z-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Ilustração Segurança */}
              <div className="relative">
                <div className="relative bg-white/70 backdrop-blur-xl border border-white/70 rounded-3xl p-8 shadow-[0_30px_70px_-20px_rgba(26,102,204,0.3),inset_0_1px_0_rgba(255,255,255,0.9)]">
                  <div className="flex gap-6 mb-8 items-end justify-center">
                    <div className="w-32 h-40 bg-slate-50 rounded-xl border border-slate-100 flex flex-col p-4 opacity-70">
                      <div className="w-full h-2 bg-slate-200 rounded-full mb-3" />
                      <div className="w-2/3 h-2 bg-slate-200 rounded-full mb-3" />
                      <div className="w-3/4 h-2 bg-slate-200 rounded-full mb-8" />
                      <div className="mt-auto w-full flex justify-center"><Lock className="size-6 text-slate-300" /></div>
                    </div>
                    <div className="w-40 h-48 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center relative z-10">
                      <ShieldCheck className="size-16 text-[#3399FF]" />
                      <div className="absolute -bottom-4 bg-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm border border-blue-50 text-slate-700">Seu navegador</div>
                    </div>
                    <div className="w-32 h-40 bg-slate-900 rounded-xl flex flex-col p-4 gap-3 border border-slate-800">
                      <div className="w-full flex-1 bg-slate-800 rounded flex items-center px-2 gap-2"><div className="size-1.5 rounded-full bg-emerald-400"/><div className="w-1/2 h-1 bg-slate-700 rounded-full"/></div>
                      <div className="w-full flex-1 bg-slate-800 rounded flex items-center px-2 gap-2"><div className="size-1.5 rounded-full bg-emerald-400"/><div className="w-1/2 h-1 bg-slate-700 rounded-full"/></div>
                      <div className="w-full flex-1 bg-slate-800 rounded flex items-center px-2 gap-2"><div className="size-1.5 rounded-full bg-slate-600"/><div className="w-1/2 h-1 bg-slate-700 rounded-full"/></div>
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm border border-blue-50 text-slate-700 whitespace-nowrap"><span className="text-red-500 font-bold mr-1">X</span> Nossos servidores</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Textos */}
              <div>
                <div className="text-xs font-bold tracking-widest text-blue-600 dark:text-blue-500 uppercase mb-2">Arquitetura Privada</div>
                <h2 id="seguranca-heading" className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-6 lg:mb-8">Segurança e privacidade</h2>
                
                <div className="space-y-4">
                  {[
                    { icon: MonitorCheck, title: "Processamento local", desc: "Seus arquivos são processados no seu navegador. Não há envio de dados para nossos servidores.", color: "bg-blue-500" },
                    { icon: ShieldCheck, title: "Zero persistência", desc: "Após o uso, os dados são descartados. A sessão é limpa automaticamente.", color: "bg-emerald-500" },
                    { icon: Lock, title: "Privacidade garantida", desc: "Coletamos apenas os dados de autenticação necessários para o serviço.", color: "bg-violet-500" },
                    { icon: Info, title: "Seu controle total", desc: "Seus arquivos não são utilizados para treinamento de modelos de IA.", color: "bg-amber-500" },
                  ].map((s, i) => (
                    <article key={i} className="flex gap-4 bg-white/70 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 group">
                      <div className={`shrink-0 size-10 rounded-xl ${s.color} grid place-items-center shadow-md group-hover:scale-110 transition-transform`}>
                        <s.icon aria-hidden="true" className="size-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-slate-900 mb-0.5">{s.title}</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LGPD */}
        <section aria-labelledby="lgpd-heading" className="py-16 lg:py-24 relative z-10">
          <div className="absolute inset-0 bg-white/50 dark:bg-[#060a13]/80 backdrop-blur-lg border-y border-white/50 dark:border-white/10 -z-10" />
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div>
              <div className="text-xs font-bold tracking-widest text-emerald-600 dark:text-emerald-400 uppercase mb-2">Conformidade Legal</div>
              <h2 id="lgpd-heading" className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white mb-4 lg:mb-6">Alinhado à LGPD</h2>
              <p className="text-sm lg:text-base text-slate-600 dark:text-slate-400 mb-6 lg:mb-8">Implementamos práticas rigorosas compatíveis com a Lei Geral de Proteção de Dados.</p>
              
              <div className="space-y-5">
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div><strong className="block text-slate-900 dark:text-slate-100 text-sm">Segurança por design</strong><span className="block mt-1 text-xs text-slate-500 dark:text-slate-400">Privacidade integrada desde o desenvolvimento da aplicação.</span></div>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div><strong className="block text-slate-900 dark:text-slate-100 text-sm">Direitos do titular</strong><span className="block mt-1 text-xs text-slate-500 dark:text-slate-400">Você tem controle sobre seus dados e pode encerrar a sessão a qualquer momento.</span></div>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div><strong className="block text-slate-900 dark:text-slate-100 text-sm">Transparência</strong><span className="block mt-1 text-xs text-slate-500 dark:text-slate-400">Informações claras sobre como seus dados são tratados e protegidos.</span></div>
                </div>
              </div>
            </div>

            <div className="bg-white/50 backdrop-blur-xl dark:bg-slate-950 p-8 rounded-2xl border border-white/70 dark:border-slate-800 flex flex-col gap-4 shadow-[0_25px_60px_-20px_rgba(26,102,204,0.25),inset_0_1px_0_rgba(255,255,255,0.85)]">
              <Link to="/privacidade" className="group bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md transition-all flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-blue-600 dark:text-blue-400 text-sm mb-0.5">Política de Privacidade</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Como coletamos, usamos e protegemos seus dados.</p>
                </div>
                <ArrowRight className="size-4 text-slate-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
              </Link>
              <Link to="/termos" className="group bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md transition-all flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-blue-600 dark:text-blue-400 text-sm mb-0.5">Termos de Uso</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Regras de uso e responsabilidades da plataforma.</p>
                </div>
                <ArrowRight className="size-4 text-slate-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
              </Link>
              <div className="group bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm transition-all flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-blue-600 dark:text-blue-400 text-sm mb-0.5">Cookies</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Informações sobre o uso de cookies e tecnologias.</p>
                </div>
              </div>
              <div id="contato" className="group bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm transition-all flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-blue-600 dark:text-blue-400 text-sm mb-0.5">DPO e Contato</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Fale com nosso Encarregado de Proteção de Dados.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Bottom */}
        <section aria-label="Chamada para Ação" className="py-16 lg:py-20 relative z-10">
          <div className="max-w-5xl mx-auto px-4 lg:px-6">
            <div className="bg-[linear-gradient(135deg,#1A66CC,#0EA5E9,#1A66CC)] rounded-[2rem] p-10 lg:p-14 text-center flex flex-col items-center justify-center gap-6 shadow-[0_40px_80px_-20px_rgba(26,102,204,0.6)] relative overflow-hidden hover:-translate-y-1 transition-all duration-500">
              {/* Orbs decorativos */}
              <div className="absolute -top-20 -right-20 size-60 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 size-60 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(51,204,255,0.15),transparent_60%)]" />
              <div className="relative z-10 flex flex-col items-center">
                <div className="size-16 rounded-full bg-white/15 grid place-items-center border border-white/30 mb-6 shadow-inner backdrop-blur-sm">
                  <ShieldCheck className="size-8 text-white" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4 tracking-tight">Inicie seu processamento com segurança</h2>
                <p className="text-base lg:text-lg text-blue-100 max-w-xl mx-auto mb-8">
                  Crie sua conta agora mesmo. Dados processados localmente, sem persistência e com total privacidade técnica.
                </p>
                <Button asChild size="lg" className="bg-white hover:bg-blue-50 border-0 text-[#1A66CC] font-bold shadow-lg shadow-blue-900/30 px-8 h-14 text-base w-full sm:w-auto transition-all hover:scale-105">
                  <Link to="/signup">Começar Agora <ArrowRight className="ml-2 size-5" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer aria-label="Rodapé Principal" className="bg-slate-50/50 dark:bg-[#060a13]/50 backdrop-blur-xl text-slate-600 dark:text-slate-400 py-12 lg:py-16 border-t border-slate-200/60 dark:border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="col-span-2 lg:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <div className="size-9 rounded-xl grid place-items-center bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] shadow-lg shadow-blue-500/25">
                <FileBox className="size-4 text-white" />
              </div>
              <div>
                <div className="font-bold text-base leading-tight text-slate-900 dark:text-white">Extrator Fiscal</div>
                <div className="text-[9px] uppercase font-semibold text-slate-500 tracking-wider">NF-e / NFC-e</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed">
              Extraia, valide e analise seus documentos fiscais com segurança, privacidade e transparência.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 text-sm">Produto</h3>
            <ul className="space-y-3 text-xs">
              <li><a href="#recursos" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Recursos</a></li>
              <li><a href="#como-funciona" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Como funciona</a></li>
              <li><Link to="/atualizacoes" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Atualizações</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 text-sm">Institucional</h3>
            <ul className="space-y-3 text-xs">
              <li><Link to="/sobre" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Sobre nós</Link></li>
              <li><a href="#seguranca" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Segurança</a></li>
              <li><Link to="/privacidade" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Privacidade</Link></li>
              <li><a href="#contato" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Contato</a></li>
            </ul>
          </div>

          <div id="contato">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 text-sm">Legal</h3>
            <ul className="space-y-3 text-xs">
              <li><Link to="/privacidade" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Política de Privacidade</Link></li>
              <li><Link to="/termos" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors">Termos de Uso</Link></li>
              <li><Link to="/cookies" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors cursor-pointer">Cookies</Link></li>
              <li><Link to="/contato-dpo" className="hover:text-blue-600 dark:hover:text-[#33CCFF] transition-colors cursor-pointer">DPO e Contato</Link></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-slate-200/60 dark:border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Extrator Fiscal. Todos os direitos reservados.</div>
          <div className="text-center max-w-2xl mx-auto md:mx-0 text-[10px] leading-relaxed">
            <strong>AVISO LEGAL E NATUREZA DO SERVIÇO:</strong> O Extrator Fiscal é EXCLUSIVAMENTE uma ferramenta de Tecnologia da Informação (SaaS). <strong>NÃO somos um escritório de contabilidade digital</strong> e não prestamos serviços de assessoria ou consultoria contábil/tributária. Os relatórios gerados <strong>não substituem</strong> a análise, validação e assinatura de um <strong>Profissional da Contabilidade</strong> regularmente inscrito no CRC. A responsabilidade por decisões fiscais e tributárias permanece integralmente do usuário corporativo e de seu respectivo contador responsável.
          </div>
        </div>
      </footer>
    </div>
  );
}
